import React, { useEffect, useRef, useState } from "react";
import { ChevronRight } from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Avatar,
  Box,
  Button,
  Divider,
  IconButton,
  TextField,
  Typography,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import AiIcon from "assets/icons/ai-icon.svg";
import AiAvatarImage from "assets/icons/ai.png";
import ClientAvatarImage from "assets/icons/client.png";
import LargeScreenIcon from "assets/icons/large-screen.svg";
import NewCrossIcon from "assets/icons/newcross.svg";
import SendIcon from "assets/icons/Send.svg";
import SmallScreenIcon from "assets/icons/small-screen.svg";
import { getObservationImages } from "services/ObservationService";
import { analyzeInspection, sendAiChat } from "services/AIAssistantService";
import "./AIAssistantDrawer.css";

import logger from "main/shared/logger";
const MAX_VISIBLE_PHOTOS = 5;

const AIAssistantDrawer = ({ selectedAiRow, inspectionId, onClose }) => {
  const [expandedComment, setExpandedComment] = useState("comment-1");
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [messageInput, setMessageInput] = useState("");
  const [chatMessages, setChatMessages] = useState([]);
  const [isSending, setIsSending] = useState(false);
  const [photoItems, setPhotoItems] = useState([]);
  const [photosLoading, setPhotosLoading] = useState(false);
  const [reasoning, setReasoning] = useState(null);
  const [reasoningLoading, setReasoningLoading] = useState(false);
  const drawerContentRef = useRef(null);
  const tierLabel = String(selectedAiRow?.tier || "A");
  const tierPrefix = tierLabel.trim().charAt(0).toUpperCase() || "A";
  const toPreviewText = (text) => {
    if (!text) return "Tap to view comment details";
    return text.length > 52 ? `${text.slice(0, 52)}...` : text;
  };
  const commentsData = [
    {
      id: "comment-1",
      title: "Comment 1",
      content: selectedAiRow?.sample_note || "No sample note available.",
      subtitle: toPreviewText(selectedAiRow?.sample_note),
    },
    {
      id: "comment-2",
      title: "Comment 2",
      content: selectedAiRow?.result || "No additional comments available.",
      subtitle: toPreviewText(selectedAiRow?.result),
    },
  ];
  const observationId = selectedAiRow?.observation_id;

  useEffect(() => {
    setChatMessages([]);
    setMessageInput("");
  }, [selectedAiRow?.id]);

  useEffect(() => {
    let isActive = true;
    if (!observationId) {
      setPhotoItems([]);
      return undefined;
    }

    setPhotosLoading(true);
    getObservationImages(observationId)
      .then((res) => {
        if (!isActive) return;
        const images = Array.isArray(res?.data?.data) ? res.data.data : [];
        setPhotoItems(
          images.map((image, index) => ({
            id: image?.id ?? `ph-${index}`,
            src: image?.url,
            alt: image?.caption || `observation photo ${index + 1}`,
            rotation: image?.rotation || 0,
          }))
        );
      })
      .catch((err) => {
        if (!isActive) return;
        logger.error("Error fetching observation images:", err);
        setPhotoItems([]);
      })
      .finally(() => {
        if (isActive) setPhotosLoading(false);
      });

    return () => {
      isActive = false;
    };
  }, [observationId]);

  useEffect(() => {
    let isActive = true;
    if (!inspectionId) {
      setReasoning(null);
      return undefined;
    }

    setReasoningLoading(true);
    analyzeInspection(inspectionId)
      .then((res) => {
        if (!isActive) return;
        const recommendation = res?.data?.data?.recommendation || null;
        setReasoning(recommendation);
        if (recommendation?.reasoning) {
          setChatMessages([
            {
              id: "ai-initial",
              role: "ai",
              text: recommendation.reasoning,
            },
          ]);
        }
      })
      .catch((err) => {
        if (!isActive) return;
        logger.error("Error analyzing inspection:", err);
        setReasoning(null);
      })
      .finally(() => {
        if (isActive) setReasoningLoading(false);
      });

    return () => {
      isActive = false;
    };
  }, [inspectionId]);

  useEffect(() => {
    if (drawerContentRef.current) {
      drawerContentRef.current.scrollTo({
        top: drawerContentRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  }, [chatMessages]);

  const handleSendMessage = async () => {
    const trimmed = messageInput.trim();
    if (!trimmed || isSending) return;

    setMessageInput("");
    setIsSending(true);
    setChatMessages((prev) => [
      ...prev,
      {
        id: `user-${Date.now()}`,
        role: "user",
        text: trimmed,
      },
    ]);

    try {
      const res = await sendAiChat({
        inspectionId,
        message: trimmed,
      });
      const aiResponse = res?.data?.data?.response;
      if (aiResponse) {
        setChatMessages((prev) => [
          ...prev,
          {
            id: `ai-${Date.now()}`,
            role: "ai",
            text: aiResponse,
          },
        ]);
      }
    } catch (err) {
      logger.error("Error sending AI chat message:", err);
      setChatMessages((prev) => [
        ...prev,
        {
          id: `ai-error-${Date.now()}`,
          role: "ai",
          text: "Sorry, something went wrong while processing your message. Please try again.",
        },
      ]);
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Box className={`ai-assistant-panel ${isFullscreen ? "fullscreen" : ""}`}>
      {/* <img
        src={LeftCloseBarIcon}
        className="test-results-close-btn"
        onClick={onClose}
        alt="close"
      /> */}
      <Box className="ai-assistant-header">
        <Box className="ai-assistant-title-pill">
          <img src={AiIcon} alt="AI icon" className="ai-header-icon" />
        </Box>
        <Box className="ai-assistant-header-actions">
          <IconButton
            type="button"
            className="ai-header-action-btn"
            aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
            onClick={() => setIsFullscreen((prev) => !prev)}
          >
            <img
              src={isFullscreen ? SmallScreenIcon : LargeScreenIcon}
              alt={isFullscreen ? "exit fullscreen" : "fullscreen"}
              className={
                isFullscreen
                  ? "ai-header-action-icon-small"
                  : "ai-header-action-icon-large"
              }
            />
          </IconButton>
          <img
            src={NewCrossIcon}
            className="close-test-results"
            onClick={onClose}
            alt="close"
          />
        </Box>
      </Box>
      <Box className="ai-assistant-content" ref={drawerContentRef}>
        <Box className="ai-context-box">
          <Box className="ai-row-summary">
            <Avatar className="ai-summary-tier">
              <span className="ai-tier-value">{tierPrefix}</span>
              <span className="ai-tier-text">Tier</span>
            </Avatar>
            <Box className="ai-summary-item">
              <Typography component="span">Room</Typography>
              <Tooltip
                title={selectedAiRow?.room_name?.name || "-"}
                arrow
                placement="bottom"
              >
                <Typography component="p">
                  {selectedAiRow?.room_name?.name || "-"}
                </Typography>
              </Tooltip>
            </Box>
            <Box className="ai-summary-item">
              <Typography component="span">Sample Type</Typography>
              <Tooltip
                title={selectedAiRow?.test?.name || "-"}
                arrow
                placement="bottom"
              >
                <Typography component="p">
                  {selectedAiRow?.test?.name || "-"}
                </Typography>
              </Tooltip>
            </Box>
            <Box className="ai-summary-item">
              <Typography component="span">Observation</Typography>
              <Tooltip
                title={selectedAiRow?.observation?.name || "-"}
                arrow
                placement="bottom"
              >
                <Typography component="p">
                  {selectedAiRow?.observation?.name || "-"}
                </Typography>
              </Tooltip>
            </Box>
            <Box
              className={`ai-summary-chip ai-summary-status ${selectedAiRow?.status ? "approved" : "unapproved"}`}
            >
              {selectedAiRow?.status ? "Approved" : "Unapproved"}
            </Box>
          </Box>

          <Divider
            className="ai-row-summary-divider"
            sx={{ borderColor: "#d9d9d9", borderBottomWidth: "medium" }}
          />

          <Box className="ai-section">
            <Typography variant="h4" className="ai-section-title">
              Photos
            </Typography>
            {photosLoading ? (
              <Box className="ai-photos-loader">
                <CircularProgress size={26} thickness={5} />
              </Box>
            ) : photoItems.length === 0 ? (
              <Typography component="p" className="ai-reason-content">
                No photos available for this observation.
              </Typography>
            ) : (
              <Box className="ai-photos-grid">
                {photoItems.slice(0, MAX_VISIBLE_PHOTOS).map((photo) => (
                  <Box key={photo.id} className="ai-photo-thumb">
                    <img
                      src={photo.src}
                      alt={photo.alt}
                      style={{ transform: `rotate(${photo.rotation}deg)` }}
                    />
                  </Box>
                ))}
                {photoItems.length > MAX_VISIBLE_PHOTOS && (
                  <Box className="ai-photo-thumb ai-photo-more">
                    <Typography component="p" className="ai-photo-more-text">
                      +{photoItems.length - MAX_VISIBLE_PHOTOS} Photos
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </Box>

          <Box className="ai-section">
            <Typography variant="h4" className="ai-section-title">
              Comments
            </Typography>
            <Box className="ai-comment-accordion">
              {commentsData.map((comment) => (
                <Accordion
                  key={comment.id}
                  expanded={expandedComment === comment.id}
                  onChange={() =>
                    setExpandedComment((prev) =>
                      prev === comment.id ? "" : comment.id
                    )
                  }
                  disableGutters
                  elevation={0}
                  square
                  className="ai-comment-card"
                >
                  <AccordionSummary
                    className="ai-comment-toggle"
                    expandIcon={
                      <ChevronRight
                        className={`ai-chevron ${expandedComment === comment.id ? "expanded" : ""}`}
                      />
                    }
                  >
                    <Box className="ai-comment-heading">
                      <Typography component="h5" className="ai-comment-title">
                        {comment.title}
                      </Typography>
                      <Typography component="p" className="ai-comment-subtext">
                        {comment.subtitle}
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails className="ai-comment-details">
                    <Typography component="p" className="ai-comment-content">
                      {comment.content}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          </Box>

          <Box className="ai-section">
            <Typography variant="h4" className="ai-section-title">
              Reason
            </Typography>
            {reasoningLoading ? (
              <Box className="ai-photos-loader">
                <CircularProgress size={26} thickness={5} />
              </Box>
            ) : reasoning ? (
              <Box className="ai-reason-wrap">
                {(reasoning.recommendedTier ||
                  reasoning.confidenceScore != null) && (
                  <Box className="ai-reason-meta">
                    {reasoning.recommendedTier && (
                      <span className="ai-reason-chip">
                        {reasoning.recommendedTier}
                      </span>
                    )}
                    {reasoning.confidenceScore != null && (
                      <span className="ai-reason-confidence">
                        Confidence:{" "}
                        {Math.round(reasoning.confidenceScore * 100)}%
                      </span>
                    )}
                  </Box>
                )}
                {reasoning.reasoning && (
                  <Typography component="p" className="ai-reason-content">
                    {reasoning.reasoning}
                  </Typography>
                )}
                {Array.isArray(reasoning.supportingEvidence) &&
                  reasoning.supportingEvidence.length > 0 && (
                    <Box className="ai-reason-evidence">
                      <Typography component="h5" className="ai-comment-title">
                        Supporting Evidence
                      </Typography>
                      <ul className="ai-evidence-list">
                        {reasoning.supportingEvidence.map((item, index) => (
                          <li key={index}>{item}</li>
                        ))}
                      </ul>
                    </Box>
                  )}
                {reasoning.labSummary && (
                  <Box className="ai-reason-evidence">
                    <Typography component="h5" className="ai-comment-title">
                      Lab Summary
                    </Typography>
                    <Typography component="p" className="ai-reason-content">
                      {reasoning.labSummary}
                    </Typography>
                  </Box>
                )}
              </Box>
            ) : (
              <Typography component="p" className="ai-reason-content">
                No reasoning data available for this sample.
              </Typography>
            )}
          </Box>
        </Box>

        <Box className="ai-analysis-header">
          <span className="ai-analysis-line" />
          <Typography className="ai-analysis-title">
            <img src={AiIcon} alt="AI icon" className="ai-analysis-icon" />
            Analysis
          </Typography>
          <span className="ai-analysis-line" />
        </Box>
        <Box className="ai-chat-wrap">
          {chatMessages.map((message) => (
            <Box key={message.id} className={`ai-chat-row ${message.role}`}>
              {message.role === "ai" ? (
                <>
                  <Avatar
                    src={AiAvatarImage}
                    className="ai-message-avatar ai"
                    variant="rounded"
                  />
                  <Box className="ai-chat-bubble ai">{message.text}</Box>
                </>
              ) : (
                <>
                  <Box className="ai-chat-bubble user">{message.text}</Box>
                  <Avatar
                    src={ClientAvatarImage}
                    className="ai-message-avatar user"
                    variant="rounded"
                  />
                </>
              )}
            </Box>
          ))}
          {isSending && (
            <Box className="ai-chat-row ai">
              <Avatar
                src={AiAvatarImage}
                className="ai-message-avatar ai"
                variant="rounded"
              />
              <Box className="ai-chat-bubble ai">
                <CircularProgress size={16} thickness={5} />
              </Box>
            </Box>
          )}
        </Box>
        <Box className="ai-kb-wrap">
          <Button type="button" className="ai-kb-btn">
            Add to Knowledge Base
          </Button>
        </Box>

        <Box className="ai-feedback">
          <TextField
            fullWidth
            variant="standard"
            placeholder="Enter Your Feedback..."
            value={messageInput}
            onChange={(e) => setMessageInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleSendMessage();
              }
            }}
            InputProps={{ disableUnderline: true, readOnly: false }}
            disabled={isSending}
          />
          <IconButton
            type="button"
            aria-label="Send feedback"
            onClick={handleSendMessage}
            disabled={isSending}
          >
            {isSending ? (
              <CircularProgress
                size={26}
                thickness={5}
                sx={{ flexShrink: 0 }}
              />
            ) : (
              <img src={SendIcon} alt="send" className="ai-send-icon" />
            )}
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
};

export default AIAssistantDrawer;
